/**
 * Chart Snapshot Module
 * Captures the chart as an image for sharing
 */

import { elements } from '../ui/dom.js';
import { state } from '../state.js';
import { showToast } from '../ui/ui-manager.js';

/**
 * Capture the main chart and download as PNG
 */
export async function captureChartSnapshot() {
    const chartElement = elements.chartContainer || document.getElementById('chart');

    if (!chartElement) {
        showToast('Chart not found', 'error');
        return;
    }

    try {
        // Find the canvas inside the chart container (lightweight-charts creates a canvas)
        const canvas = chartElement.querySelector('canvas');

        if (!canvas) {
            showToast('Canvas not found', 'error');
            return;
        }

        // Create a copy canvas to add watermark/info
        const exportCanvas = document.createElement('canvas');
        const ctx = exportCanvas.getContext('2d');

        exportCanvas.width = canvas.width;
        exportCanvas.height = canvas.height + 40; // Extra space for watermark

        // Fill background
        ctx.fillStyle = '#0d1117';
        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

        // Draw the chart
        ctx.drawImage(canvas, 0, 0);

        // Add watermark text
        ctx.fillStyle = '#8b949e';
        ctx.font = '14px Inter, sans-serif';
        const symbol = state.currentSymbol || 'Chart';
        const date = new Date().toLocaleDateString();
        ctx.fillText(`${symbol} • ${date} • Stock Chart Viewer`, 10, canvas.height + 25);

        // Convert to blob and download
        exportCanvas.toBlob((blob) => {
            if (!blob) {
                showToast('Failed to create image', 'error');
                return;
            }

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${symbol}_chart_${Date.now()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            showToast('Chart snapshot saved!', 'success');
        }, 'image/png');

    } catch (error) {
        console.error('Snapshot error:', error);
        showToast('Snapshot failed', 'error');
    }
}

/**
 * Copy chart to clipboard (for sharing)
 */
export async function copyChartToClipboard() {
    const chartElement = elements.chartContainer || document.getElementById('chart');
    const canvas = chartElement?.querySelector('canvas');

    if (!canvas) {
        showToast('Canvas not found', 'error');
        return;
    }

    try {
        canvas.toBlob(async (blob) => {
            if (!blob) return;

            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob })
            ]);

            showToast('Chart copied to clipboard!', 'success');
        }, 'image/png');
    } catch (error) {
        console.error('Clipboard error:', error);
        showToast('Copy failed (browser may not support this)', 'error');
    }
}
